import { API_BASE_URL } from "../constants";
import type { Message, ReportCard } from "../types";

export type AdminSessionStatus = "not_submitted" | "submitted" | "graded";

export interface AdminSessionUser {
  userId: string;
  stytchMemberId: string;
  email: string | null;
  name: string | null;
  organizationId: string;
  organizationName: string | null;
  sessionCount: number;
  lastActiveAt: string;
}

export interface AdminSessionUsersPage {
  users: AdminSessionUser[];
  page: number;
  pageSize: number;
  total: number;
}

export interface AdminSessionSummary {
  sessionId: string;
  sessionNumber: number;
  name: string | null;
  scenarioId: string;
  scenarioTitle: string | null;
  createdAt: string;
  lastActiveAt: string;
  messageCount: number;
  coachTurnCount: number;
  status: AdminSessionStatus;
}

export interface AdminCoachTurn {
  assistanceType: "hint" | "feedback" | "chat";
  userPrompt: string | null;
  response: string;
  createdAt: string;
}

export interface AdminSessionPersona {
  id: string;
  name: string;
  role: string;
  avatarUrl?: string;
}

export interface AdminSessionDetail {
  session: {
    id: string;
    sessionNumber: number;
    name: string | null;
    createdAt: string;
    lastActiveAt: string;
  };
  user: {
    userId: string;
    email: string | null;
    name: string | null;
    stytchMemberId: string;
    organizationId: string;
    organizationName: string | null;
  };
  scenario: { id: string; title: string } | null;
  personas: AdminSessionPersona[];
  documents: { id: string; name: string }[];
  messages: Message[];
  coachTurns: AdminCoachTurn[];
  submission: {
    answer: string | null;
    artifactName: string | null;
    artifactUrl: string | null;
  } | null;
  reportCard: ReportCard | null;
}

function authHeaders(token?: string): HeadersInit {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (token) headers["Authorization"] = `Bearer ${token}`;
  return headers;
}

/** The server's `message` (string or Nest's validation array) when the body is JSON, else the raw text. */
function errorDetail(text: string): string {
  try {
    const message = (JSON.parse(text) as { message?: unknown } | null)?.message;
    if (typeof message === "string") return message;
    if (Array.isArray(message) && message.every((m) => typeof m === "string")) {
      return message.join(", ");
    }
  } catch {
    // not JSON; fall through to the raw text
  }
  return text;
}

async function getJson<T>(url: string, action: string, token?: string, signal?: AbortSignal): Promise<T> {
  const response = await fetch(url, { headers: authHeaders(token), signal });
  if (!response.ok) {
    throw new Error(`${action} failed (${response.status}): ${errorDetail(await response.text())}`);
  }
  return response.json();
}

/** GET /admin/sessions/users — users with at least one session, newest activity first. */
export function listSessionUsers(
  params: { page: number; search: string },
  token?: string,
  signal?: AbortSignal,
): Promise<AdminSessionUsersPage> {
  const qs = new URLSearchParams({ page: String(params.page) });
  if (params.search) qs.set("search", params.search);
  return getJson(`${API_BASE_URL}/admin/sessions/users?${qs}`, "Load users", token, signal);
}

/** GET /admin/sessions/users/:userId/sessions — every session of one user. */
export function listUserSessions(
  userId: string,
  token?: string,
  signal?: AbortSignal,
): Promise<AdminSessionSummary[]> {
  return getJson(
    `${API_BASE_URL}/admin/sessions/users/${encodeURIComponent(userId)}/sessions`,
    "Load sessions",
    token,
    signal,
  );
}

/** GET /admin/sessions/:sessionId — everything the read-only session page shows. */
export function getSessionDetail(
  sessionId: string,
  token?: string,
  signal?: AbortSignal,
): Promise<AdminSessionDetail> {
  return getJson(
    `${API_BASE_URL}/admin/sessions/${encodeURIComponent(sessionId)}`,
    "Load session",
    token,
    signal,
  );
}
